import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { LoadScript, Autocomplete } from "@react-google-maps/api";

const BASE = "https://dashboard.maineautoparts.com/api";
const ADMIN_KEY = "maineautos_admin_2024";
const GMAP_KEY = "AIzaSyDxpKBuxXpQRXU3XL97I4qqf2dbE_5M7Z0";
const LIBRARIES = ["places"];

// CDN SheetJS loader
function useXLSX(onReady) {
    const cb = useCallback(onReady, []);                  // stable ref
    useEffect(() => {
        if (window.XLSX) { cb(window.XLSX); return; }
        const s = document.createElement("script");
        s.src = "https://cdn.sheetjs.com/xlsx-latest/package/dist/xlsx.full.min.js";
        s.onload = () => cb(window.XLSX);
        document.head.appendChild(s);
    }, [cb]);
}

// ── Column definitions ────────────────────────────────────────────────────────
const COLUMNS = {
    users: [
        { key: "id", label: "ID" },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phone_number", label: "Phone" },
        { key: "state", label: "State" },
        { key: "address", label: "Address" },
        { key: "created_at", label: "Registered" },
    ],
    mechanics: [
        { key: "id", label: "ID" },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phone_number", label: "Phone" },
        { key: "specialization", label: "Speciality" },
        { key: "state", label: "State" },
        { key: "address", label: "Address" },
        { key: "created_at", label: "Registered" },
    ],
};

// ── Auth gate ─────────────────────────────────────────────────────────────────
function AuthGate({ onAuth }) {
    const [val, setVal] = useState("");
    const [err, setErr] = useState("");
    return (
        <div style={S.gate}>
            <div style={S.gateBox}>
                <div style={S.logo}>🔐</div>
                <h2 style={{ margin: "8px 0 4px", color: "#222" }}>Admin Export</h2>
                <p style={{ color: "#999", marginBottom: 24, fontSize: 14 }}>
                    Enter your admin key to continue
                </p>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    if (val === ADMIN_KEY) onAuth();
                    else setErr("Incorrect admin key. Please try again.");
                }}>
                    <input
                        type="password"
                        placeholder="Admin key"
                        value={val}
                        onChange={(e) => setVal(e.target.value)}
                        style={S.input}
                        autoFocus
                    />
                    {err && <p style={{ color: "#e55", fontSize: 13, marginTop: 6 }}>{err}</p>}
                    <button type="submit" style={{ ...S.btn, width: "100%", marginTop: 14 }}>
                        Unlock
                    </button>
                </form>
            </div>
        </div>
    );
}

// ── Main export UI ────────────────────────────────────────────────────────────
export default function AdminExport() {
    const [authed, setAuthed] = useState(false);

    if (!authed) return <AuthGate onAuth={() => setAuthed(true)} />;
    return (
        <LoadScript googleMapsApiKey={GMAP_KEY} libraries={LIBRARIES}>
            <ExportBody />
        </LoadScript>
    );
}

function ExportBody() {
    const [tab, setTab] = useState("users");
    // Location filter (from Google Maps)
    const [stateFilter, setStateFilter] = useState("");      // extracted state string
    const [placeLabel, setPlaceLabel] = useState("");      // display in input
    const autocompleteRef = useRef(null);
    // Other filters
    const [specialty, setSpecialty] = useState("");
    const [search, setSearch] = useState("");
    // Results
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(null);
    const [fetched, setFetched] = useState(false);
    const [error, setError] = useState("");

    const xlsxRef = useRef(null);
    useXLSX((lib) => { xlsxRef.current = lib; });

    // Reset when tab changes
    useEffect(() => {
        setData([]); setTotal(null); setFetched(false); setError("");
        setStateFilter(""); setPlaceLabel(""); setSpecialty(""); setSearch("");
    }, [tab]);

    // ── Google Maps place picked ────────────────────────────────────────────────
    const onPlaceChanged = () => {
        const place = autocompleteRef.current?.getPlace();
        if (!place?.address_components) return;

        setPlaceLabel(place.formatted_address || "");

        // Extract state (administrative_area_level_1)
        let state = "";
        for (const comp of place.address_components) {
            if (comp.types.includes("administrative_area_level_1")) {
                state = comp.long_name;
                break;
            }
        }
        setStateFilter(state);
    };

    // Clear location
    const clearLocation = () => {
        setStateFilter("");
        setPlaceLabel("");
    };

    // ── Fetch ───────────────────────────────────────────────────────────────────
    const fetchData = async () => {
        setLoading(true); setError("");
        try {
            const endpoint = tab === "users" ? "admin/users" : "admin/mechanics";
            const params = {};
            if (stateFilter) params.state = stateFilter;
            if (search) params.search = search;
            if (specialty && tab === "mechanics") params.specialty = specialty;

            const res = await axios.get(`${BASE}/${endpoint}`, {
                params,
                headers: { "X-Admin-Key": ADMIN_KEY },
            });

            const rows = res.data.data || [];
            setData(rows);
            setTotal(res.data.total ?? rows.length);
            setFetched(true);
        } catch (err) {
            setError("Failed to fetch: " + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    // ── Export ──────────────────────────────────────────────────────────────────
    const exportExcel = () => {
        const XLSX = xlsxRef.current;
        if (!XLSX) { alert("Excel library still loading, wait a moment."); return; }
        if (!data.length) { alert("No data to export — fetch first."); return; }

        const cols = COLUMNS[tab];
        const rows = data.map((row) => {
            const obj = {};
            cols.forEach(({ key, label }) => {
                let val = row[key] ?? "";
                if (key === "created_at" && val) val = new Date(val).toLocaleDateString();
                obj[label] = val;
            });
            return obj;
        });

        const ws = XLSX.utils.json_to_sheet(rows);
        // Auto-size columns
        const colWidths = cols.map(({ label }) => ({ wch: Math.max(label.length, 16) }));
        ws["!cols"] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, tab === "users" ? "Users" : "Mechanics");
        const filename = `${tab}_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, filename);
    };

    const cols = COLUMNS[tab];

    return (
        <div style={S.page}>
            <div style={S.container}>

                {/* Header */}
                <div style={S.header}>
                    <h2 style={{ margin: 0, color: "#222" }}>📊 Admin Data Export</h2>
                    <p style={{ margin: "4px 0 0", color: "#888", fontSize: 13 }}>
                        Search, filter and export users or mechanics to Excel
                    </p>
                </div>

                {/* Tabs */}
                <div style={S.tabs}>
                    {[
                        { key: "users", label: "👤 Users" },
                        { key: "mechanics", label: "🔧 Mechanics" },
                    ].map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            style={{ ...S.tabBtn, ...(tab === t.key ? S.tabActive : {}) }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Filters */}
                <div style={S.filters}>

                    {/* Google Maps location picker */}
                    <div style={S.filterGroup}>
                        <label style={S.label}>
                            📍 State / Region{" "}
                            <span style={{ color: "#bbb", fontWeight: 400 }}>(leave blank for all)</span>
                        </label>
                        <div style={{ position: "relative" }}>
                            <Autocomplete
                                onLoad={(ref) => (autocompleteRef.current = ref)}
                                onPlaceChanged={onPlaceChanged}
                            >
                                <input
                                    type="text"
                                    placeholder="Type a city or state..."
                                    value={placeLabel}
                                    onChange={(e) => {
                                        // if user clears manually, reset state filter too
                                        setPlaceLabel(e.target.value);
                                        if (!e.target.value) setStateFilter("");
                                    }}
                                    style={{ ...S.input, paddingRight: 32 }}
                                />
                            </Autocomplete>
                            {placeLabel && (
                                <button
                                    onClick={clearLocation}
                                    style={S.clearBtn}
                                    title="Clear location"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                        {stateFilter && (
                            <span style={S.statePill}>Filtering by state: <strong>{stateFilter}</strong></span>
                        )}
                    </div>

                    {/* Specialty (mechanics only) */}
                    {tab === "mechanics" && (
                        <div style={S.filterGroup}>
                            <label style={S.label}>🔩 Specialty</label>
                            <input
                                style={S.input}
                                placeholder="e.g. Engine, AC, Electrical..."
                                value={specialty}
                                onChange={(e) => setSpecialty(e.target.value)}
                            />
                        </div>
                    )}

                    {/* Name / email search */}
                    <div style={S.filterGroup}>
                        <label style={S.label}>🔍 Name / Email Search</label>
                        <input
                            style={S.input}
                            placeholder="Search by name or email..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && fetchData()}
                        />
                    </div>

                    {/* Fetch button */}
                    <div style={{ ...S.filterGroup, justifyContent: "flex-end" }}>
                        <label style={{ ...S.label, opacity: 0 }}>.</label>
                        <button onClick={fetchData} disabled={loading} style={S.btn}>
                            {loading ? "⏳ Loading..." : "Fetch Data"}
                        </button>
                    </div>
                </div>

                {/* Error */}
                {error && <div style={S.errorBox}>{error}</div>}

                {/* Stats row */}
                {fetched && (
                    <div style={S.statsRow}>
                        <div>
                            <span style={{ fontWeight: 700, fontSize: 16 }}>
                                {total?.toLocaleString()}{" "}
                            </span>
                            <span style={{ color: "#666" }}>
                                {tab === "users" ? "user" : "mechanic"}{total !== 1 ? "s" : ""} found
                                {stateFilter ? ` in "${stateFilter}"` : " (all)"}
                            </span>
                        </div>
                        <button onClick={exportExcel} style={S.exportBtn}>
                            ⬇️ Export to Excel
                        </button>
                    </div>
                )}

                {/* Table */}
                {fetched && data.length > 0 && (
                    <div style={S.tableWrap}>
                        <table style={S.table}>
                            <thead>
                                <tr>
                                    {cols.map(({ key, label }) => (
                                        <th key={key} style={S.th}>{label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, i) => (
                                    <tr key={i} style={{ background: i % 2 === 0 ? "#fafafa" : "#fff" }}>
                                        {cols.map(({ key }) => (
                                            <td key={key} style={{
                                                ...S.td,
                                                ...(key === "name" ? { fontWeight: 600 } : {}),
                                                ...(key === "email" ? { color: "#1a73e8" } : {}),
                                            }}>
                                                {key === "created_at" && row[key]
                                                    ? new Date(row[key]).toLocaleDateString("en-GB", {
                                                        day: "2-digit", month: "short", year: "numeric",
                                                    })
                                                    : (row[key] || <span style={{ color: "#bbb" }}>—</span>)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Empty */}
                {fetched && !loading && data.length === 0 && (
                    <div style={S.empty}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                        No records found for the selected filters.
                        <br />
                        <span style={{ color: "#aaa", fontSize: 13 }}>Try clearing the location filter to see all.</span>
                    </div>
                )}

            </div>
        </div>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
    page: {
        minHeight: "100vh",
        background: "#f0f2f7",
        padding: "30px 16px 60px",
        fontFamily: "'Segoe UI', Arial, sans-serif",
    },
    container: {
        maxWidth: 1100,
        margin: "0 auto",
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        padding: "32px 36px",
    },
    header: {
        borderBottom: "1px solid #eee",
        paddingBottom: 18,
        marginBottom: 24,
    },
    tabs: {
        display: "flex",
        gap: 10,
        marginBottom: 28,
    },
    tabBtn: {
        padding: "10px 24px",
        borderRadius: 8,
        border: "1.5px solid #ddd",
        cursor: "pointer",
        background: "#f4f6fb",
        fontWeight: 500,
        fontSize: 14,
        color: "#555",
        transition: "all 0.15s",
    },
    tabActive: {
        background: "#e06027",
        color: "#fff",
        border: "1.5px solid #e06027",
        boxShadow: "0 2px 10px rgba(224,96,39,0.25)",
    },
    filters: {
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
        marginBottom: 24,
        alignItems: "flex-end",
    },
    filterGroup: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 180,
    },
    label: {
        fontSize: 12,
        fontWeight: 600,
        color: "#555",
        marginBottom: 6,
        letterSpacing: "0.3px",
    },
    input: {
        padding: "10px 14px",
        borderRadius: 8,
        border: "1.5px solid #ddd",
        fontSize: 14,
        outline: "none",
        width: "100%",
        boxSizing: "border-box",
        transition: "border 0.15s",
    },
    clearBtn: {
        position: "absolute",
        right: 10,
        top: "50%",
        transform: "translateY(-50%)",
        background: "none",
        border: "none",
        cursor: "pointer",
        fontSize: 20,
        color: "#aaa",
        lineHeight: 1,
        padding: "0 4px",
    },
    statePill: {
        marginTop: 6,
        fontSize: 12,
        color: "#e06027",
        background: "#fff3ee",
        padding: "3px 10px",
        borderRadius: 20,
        display: "inline-block",
    },
    btn: {
        padding: "10px 24px",
        borderRadius: 8,
        border: "none",
        background: "#e06027",
        color: "#fff",
        fontWeight: 700,
        fontSize: 14,
        cursor: "pointer",
        whiteSpace: "nowrap",
        boxShadow: "0 2px 8px rgba(224,96,39,0.2)",
    },
    exportBtn: {
        padding: "9px 20px",
        borderRadius: 8,
        border: "none",
        background: "#1a7d4f",
        color: "#fff",
        fontWeight: 700,
        fontSize: 14,
        cursor: "pointer",
        boxShadow: "0 2px 8px rgba(26,125,79,0.2)",
    },
    statsRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 16,
        padding: "10px 0",
        borderBottom: "1px solid #f0f0f0",
    },
    tableWrap: {
        overflowX: "auto",
        borderRadius: 10,
        border: "1px solid #eee",
        marginTop: 8,
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 13,
    },
    th: {
        padding: "12px 14px",
        background: "#f7f8fa",
        textAlign: "left",
        fontWeight: 700,
        color: "#444",
        whiteSpace: "nowrap",
        borderBottom: "2px solid #eee",
    },
    td: {
        padding: "10px 14px",
        color: "#333",
        borderBottom: "1px solid #f0f0f0",
        maxWidth: 200,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    empty: {
        textAlign: "center",
        padding: "50px 20px",
        color: "#888",
        fontSize: 15,
    },
    errorBox: {
        background: "#fff5f5",
        border: "1px solid #fcc",
        borderRadius: 8,
        padding: "12px 16px",
        color: "#c0392b",
        marginBottom: 16,
        fontSize: 14,
    },
    // Auth gate
    gate: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f0f2f7 0%, #e8ecf5 100%)",
        fontFamily: "'Segoe UI', Arial, sans-serif",
    },
    gateBox: {
        background: "#fff",
        padding: "44px 40px 36px",
        borderRadius: 16,
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        minWidth: 340,
        textAlign: "center",
    },
    logo: {
        fontSize: 44,
        lineHeight: 1,
        marginBottom: 4,
    },
};
